import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { LearningContentAPI } from '@chancenwerk/campus-learning-content/api/LearningContentAPI.js';
import { CampusEvent } from '@chancenwerk/campus-learning-content/entities/CampusEvent.js';
import { CampusEventFilter } from '@chancenwerk/campus-learning-content/entities/CampusEventFilter.js';
import { CampusArea } from '@chancenwerk/campus-learning-content/entities/CampusSection.js';
import { Role } from '@chancenwerk/db/semantics/Role.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-image.js';
import '@chancenwerk/design/components/cr-input.js';
import '@chancenwerk/design/components/fonts.js';
import { type ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { combineLatest, filter, switchMap, takeUntil } from 'rxjs';
import './campus-component-eventbox.js';

@customElement('campus-component-eventlist')
export class CampusComponentEventlist extends ChemistryLitElement {
  @property({ type: Boolean }) editingMode = false;
  @property({ attribute: false }) public area = new RXController(this, CampusArea.STUDENTS);

  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());

  @state() private readonly _filter = new RXController(this, new CampusEventFilter({}));

  @state() private readonly _events = new RXController(
    this,
    this._filter.observable.pipe(
      filter(Boolean),
      switchMap(filter => LearningContentAPI.subscribeEventsByFilter(0, 0, filter)(CampusEvent.allFields)),
    ),
  );

  override connectedCallback(): void {
    super.connectedCallback();
    combineLatest([this._currentUser.observable, this.area.observable])
      .pipe(filter(Boolean), takeUntil(this.disconnected))
      .subscribe(([user, area]) => this._filter.next(new CampusEventFilter({ forUserID: user?._id, area })));
  }

  override render() {
    if (!this._events.value) return nothing;
    return html`
      <cr-flex wrap space="sm" horizontal margin="md">
        ${this._currentUser.value?.checkRole(Role.STUDENT)
          ? this._filterEvents(this._events.value).map(
              event =>
                html`
                  <campus-component-eventbox
                    ?editingmode=${this.editingMode}
                    .event=${event}
                  ></campus-component-eventbox>
                `,
            )
          : this._events.value.map(
              event =>
                html`
                  <campus-component-eventbox
                    ?editingmode=${this.editingMode}
                    .event=${event}
                  ></campus-component-eventbox>
                `,
            )}
      </cr-flex>
    `;
  }

  private _filterEvents(events: CampusEvent[]) {
    return events
      .filter(
        event =>
          event.startDate.startOf('day').toMillis() < DateTime.now().toMillis() &&
          event.endDate.endOf('day').toMillis() > DateTime.now().toMillis(),
      )
      .sort((a, b) => a.startDate.toMillis() - b.startDate.toMillis());
  }
}
